import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Play, 
  Star, 
  Clock, 
  Calendar, 
  Sparkles, 
  Tv, 
  Film,
  Layers,
  CheckCircle2,
  Share2,
  Subtitles,
  Info
} from 'lucide-react';
import { MediaItem, EpisodeInfo, WatchHistoryItem } from '../types';
import { cleanGenres } from '../utils/genreUtils';
import { SubdlSubtitleModal } from './SubdlSubtitleModal';
import { TV_KEYS, TV_KEYCODES } from '../utils/deviceDetection';

interface MediaDetailModalProps {
  item: MediaItem | null;
  onClose: () => void;
  onPlay: (item: MediaItem, episodeIndex?: number, initialTime?: number) => void;
  onToggleWatchlist?: (item: MediaItem) => void;
  isInWatchlist?: boolean;
  historyMap?: Record<string, WatchHistoryItem>;
}

export const MediaDetailModal: React.FC<MediaDetailModalProps> = ({
  item,
  onClose,
  onPlay,
  historyMap = {}
}) => {
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isSubdlOpen, setIsSubdlOpen] = useState(false);
  const [subdlEpisode, setSubdlEpisode] = useState<EpisodeInfo | null>(null);
  const [detailEpisode, setDetailEpisode] = useState<{ ep: EpisodeInfo; targetIndex: number; progressSeconds: number } | null>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Group episodes by season if series
  const episodes = item?.episodes || [];
  const seasons = Array.from(new Set(episodes.map(e => Number(e.season) || 1))).sort((a: number, b: number) => a - b);
  if (seasons.length === 0 && item?.type === 'series') seasons.push(1);

  const filteredEpisodes = episodes.filter(e => (e.season || 1) === selectedSeason);

  // Resume item from history
  const historyForMedia = item ? historyMap[item.id] : undefined;
  const resumeEpisodeIndex = historyForMedia?.episode
    ? episodes.findIndex(e => (Number(e.season) || 1) === (Number(historyForMedia.season) || 1) && (Number(e.episode) || 1) === (Number(historyForMedia.episode) || 1))
    : -1;
  const primaryEpIdx = resumeEpisodeIndex >= 0 ? resumeEpisodeIndex : 0;
  const primaryResumeTime = historyForMedia?.progressSeconds || 0;

  // Auto-focus on primary action button when modal opens
  useEffect(() => {
    if (!item) return;

    const previousFocus = document.activeElement as HTMLElement | null;

    const timer = setTimeout(() => {
      const playBtn = document.querySelector<HTMLElement>('#modal-play-primary, #close-modal-button');
      if (playBtn) {
        playBtn.focus();
        playBtn.classList.add('tv-focused');
      }
    }, 60);

    return () => {
      clearTimeout(timer);
      if (previousFocus && typeof previousFocus.focus === 'function') {
        try {
          previousFocus.focus();
          previousFocus.classList.add('tv-focused');
        } catch {
          // Ignore
        }
      }
    };
  }, [item]);

  // Focus helper with smooth scroll into view
  const focusElement = useCallback((selectorOrEl: string | HTMLElement | null) => {
    const el = typeof selectorOrEl === 'string' ? document.querySelector<HTMLElement>(selectorOrOrNull(selectorOrEl)) : selectorOrEl;
    if (el) {
      el.focus();
      el.classList.add('tv-focused');
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, []);

  function selectorOrOrNull(sel: string): string {
    return sel;
  }

  // ESC and Back button handler for internal sub-modals (Detail sheet, SubDL)
  useEffect(() => {
    if (!item) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key || '';
      const code = (e as any).keyCode || e.which || 0;

      // Back navigation (ESC, Android Back, Tizen/WebOS Back) for internal sub-modals
      if (
        TV_KEYS.BACK.includes(key) ||
        TV_KEYCODES.BACK.includes(code) ||
        key === 'Escape' ||
        key === 'Backspace' ||
        code === 10009 ||
        code === 461
      ) {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag !== 'input' && tag !== 'textarea') {
          if (detailEpisode) {
            e.preventDefault();
            e.stopPropagation();
            setDetailEpisode(null);
            setTimeout(() => focusElement('#modal-ep-play-0'), 50);
          } else if (isSubdlOpen) {
            e.preventDefault();
            e.stopPropagation();
            setIsSubdlOpen(false);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [item, detailEpisode, isSubdlOpen, focusElement]);

  if (!item) return null;

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  return (
    <div 
      id="media-detail-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto bg-black/90 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div 
        ref={modalContainerRef}
        className="relative w-full max-w-5xl max-h-[92vh] flex flex-col bg-[#0a0e18] rounded-2xl border border-sky-500/25 shadow-2xl shadow-black my-auto text-left overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Backdrop Banner */}
        <div className="relative h-28 sm:h-36 md:h-40 w-full shrink-0 overflow-hidden bg-slate-900">
          <img
            src={item.backdrop || item.poster}
            alt={item.title}
            className="w-full h-full object-cover object-top opacity-60"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e18] via-[#0a0e18]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e18]/90 via-[#0a0e18]/40 to-transparent" />

          {/* Close Button */}
          <button
            id="close-modal-button"
            data-focusable="true"
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-black/80 hover:bg-sky-500 hover:text-black text-white border border-white/20 transition-all z-20 backdrop-blur-md cursor-pointer outline-none focus:ring-2 focus:ring-sky-400 focus:bg-sky-500 focus:text-black"
            title="Tancar (ESC)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body: Left column (Series info) and Right column (Seasons & Episodes) */}
        <div className="px-4 sm:px-6 pb-6 -mt-12 sm:-mt-16 relative z-10 flex-1 overflow-y-auto">
          {item.type === 'series' && episodes.length > 0 ? (
            /* SIDE-BY-SIDE 2-COLUMN LAYOUT */
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              
              {/* LEFT COLUMN: Poster, Title, Meta, Primary Play / Resume, SubDL, Share, Synopsis */}
              <div className="md:col-span-5 lg:col-span-4 space-y-4">
                {/* Poster & Main Header */}
                <div className="flex gap-3.5 items-start">
                  <div className="w-24 sm:w-28 md:w-32 aspect-[2/3] rounded-xl overflow-hidden shadow-xl shadow-black border border-white/20 shrink-0 bg-slate-800">
                    <img
                      src={item.poster}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </div>

                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-sky-500 text-black">
                        SÈRIE
                      </span>
                      {item.collectionName && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30 flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          <span className="truncate max-w-[110px]">{item.collectionName}</span>
                        </span>
                      )}
                    </div>

                    <h2 className="font-display font-bold text-lg sm:text-xl text-white tracking-tight leading-tight line-clamp-2">
                      {item.title}
                    </h2>

                    {item.originalTitle && item.originalTitle !== item.title && (
                      <p className="text-[11px] text-slate-400 font-jp truncate">
                        {item.originalTitle}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-300 pt-0.5">
                      {item.rating && (
                        <div className="flex items-center gap-1 text-sky-400 font-bold bg-sky-500/10 px-1.5 py-0.5 rounded border border-sky-500/20">
                          <Star className="w-3 h-3 fill-sky-400" />
                          <span>{item.rating.toFixed(1)}</span>
                        </div>
                      )}
                      {item.year && (
                        <div className="flex items-center gap-1 text-slate-400">
                          <Calendar className="w-3 h-3" />
                          <span>{item.year}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span>{episodes.length} ep.</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Primary Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    id="modal-play-primary"
                    data-focusable="true"
                    type="button"
                    onClick={() => onPlay(item, primaryEpIdx, primaryResumeTime)}
                    className="flex-1 min-w-[140px] px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs shadow-lg shadow-sky-500/25 transition-all flex items-center justify-center gap-2 cursor-pointer outline-none focus:ring-2 focus:ring-sky-400 focus:scale-[1.02]"
                  >
                    <Play className="w-3.5 h-3.5 fill-white" />
                    <span className="truncate">
                      {primaryResumeTime > 0 
                        ? `Reprendre (Ep. ${episodes[primaryEpIdx]?.episode || 1})` 
                        : 'Reprodueix Ep. 1'}
                    </span>
                  </button>

                  <button
                    id="modal-subdl-button"
                    data-focusable="true"
                    type="button"
                    tabIndex={0}
                    onClick={() => setIsSubdlOpen(true)}
                    className="px-3 py-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 border border-amber-500/40 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer outline-none focus:ring-2 focus:ring-amber-400"
                    title="Cercar subtítols a SubDL"
                  >
                    <Subtitles className="w-3.5 h-3.5 text-amber-400" />
                    <span>SubDL</span>
                  </button>

                  <button
                    id="modal-share-button"
                    data-focusable="true"
                    type="button"
                    tabIndex={0}
                    onClick={handleShare}
                    className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 text-xs font-semibold transition-all flex items-center justify-center cursor-pointer outline-none focus:ring-2 focus:ring-sky-400"
                    title="Copiar enllaç"
                  >
                    {copiedLink ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* Synopsis */}
                <div className="space-y-1.5 pt-2 border-t border-white/10">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sinopsi</h3>
                  <p className="text-xs text-slate-300 leading-relaxed max-h-48 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/10">
                    {item.summary || "Sense sinopsi disponible."}
                  </p>
                </div>
              </div>

              {/* RIGHT COLUMN: Seasons & Episodes List */}
              <div className="md:col-span-7 lg:col-span-8 space-y-3 md:pl-4 md:border-l md:border-white/10 flex flex-col">
                {/* Header & Season Selector */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <Tv className="w-4 h-4 text-sky-400" />
                    <h3 className="font-display font-bold text-sm text-white">Temporades i Episodis</h3>
                    <span className="text-xs text-slate-400 font-medium">({filteredEpisodes.length} capítols)</span>
                  </div>

                  {/* Season tabs */}
                  {seasons.length > 1 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1 max-w-[220px] sm:max-w-xs scrollbar-none">
                      {seasons.map(s => (
                        <button
                          key={s}
                          id={`modal-season-${s}`}
                          data-focusable="true"
                          type="button"
                          tabIndex={0}
                          onClick={() => setSelectedSeason(s)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedSeason(s);
                            }
                          }}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all outline-none cursor-pointer focus:ring-2 focus:ring-sky-400 ${
                            selectedSeason === s
                              ? 'bg-sky-500 text-black shadow-md'
                              : 'bg-white/5 text-slate-400 hover:text-white border border-white/5'
                          }`}
                        >
                          T{s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Episodes Vertical List with Dedicated D-Pad Action Buttons */}
                <div 
                  id="modal-episodes-list-container"
                  className="space-y-2 max-h-[50vh] md:max-h-[56vh] overflow-y-auto pr-1.5 scrollbar-thin scrollbar-thumb-white/15"
                >
                  {filteredEpisodes.map((ep, idx) => {
                    const directIdx = episodes.indexOf(ep);
                    const matchedIdx = directIdx >= 0 ? directIdx : episodes.findIndex(e => 
                      (ep.id && e.id === ep.id) || 
                      (Number(e.season || 1) === Number(ep.season || 1) && Number(e.episode) === Number(ep.episode))
                    );
                    const targetIndex = matchedIdx >= 0 ? matchedIdx : idx;

                    const epHistory = historyMap[`${item.id}_s${ep.season || 1}_e${ep.episode}`] || 
                      (historyForMedia?.episode === ep.episode && (historyForMedia.season || 1) === (ep.season || 1) ? historyForMedia : null);
                    const isFinished = epHistory && epHistory.progressSeconds > 100 && (epHistory.durationSeconds ? epHistory.progressSeconds / epHistory.durationSeconds > 0.85 : false);
                    const epImage = ep.poster || (ep as any).movie_image || (ep as any).image;
                    const epPlot = ep.plot || (ep as any).summary;

                    return (
                      <div
                        key={ep.id || `${ep.season || 1}-${ep.episode}-${idx}`}
                        id={`modal-episode-row-${idx}`}
                        data-episode-index={idx}
                        className="group flex items-center justify-between p-2.5 rounded-xl bg-white/5 hover:bg-sky-500/10 border border-white/5 hover:border-sky-500/30 has-[:focus]:border-sky-400 has-[:focus]:bg-sky-500/15 has-[:focus]:ring-1 has-[:focus]:ring-sky-400/40 transition-all text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {/* Episode Thumbnail / Number */}
                          {epImage ? (
                            <div className="relative w-16 h-10 sm:w-20 sm:h-12 rounded-lg overflow-hidden shrink-0 bg-slate-800 border border-white/10">
                              <img
                                src={epImage}
                                alt=""
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  (e.currentTarget as HTMLElement).style.display = 'none';
                                }}
                              />
                              <span className="absolute bottom-0.5 left-0.5 px-1 py-0.2 rounded bg-black/80 font-mono text-[9px] font-bold text-white">
                                {ep.episode}
                              </span>
                            </div>
                          ) : (
                            <span className="w-6 text-center text-xs font-mono font-bold text-slate-400 group-hover:text-sky-400 shrink-0">
                              {ep.episode}
                            </span>
                          )}

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-white truncate group-hover:text-sky-300">
                              {ep.title || `Episodi ${ep.episode}`}
                            </p>
                            <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                              {(ep.duration || ep.runtime) && (
                                <span>{ep.duration || ep.runtime} min</span>
                              )}
                              {ep.rating && (
                                <span className="text-amber-400 font-bold">★ {Number(ep.rating).toFixed(1)}</span>
                              )}
                              {ep.air_date && (
                                <span>• {ep.air_date.slice(0, 4)}</span>
                              )}
                            </div>
                            {epPlot && (
                              <p className="text-[10px] text-slate-400/90 line-clamp-1 mt-0.5 font-normal">
                                {epPlot}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Episode Action Buttons (Play, SubDL, Details) */}
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {/* Play Button */}
                          <button
                            id={`modal-ep-play-${idx}`}
                            data-focusable="true"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPlay(item, targetIndex, epHistory?.progressSeconds || 0);
                            }}
                            title={`Reproduir episodi ${ep.episode}`}
                            className={`p-2 rounded-full transition-all cursor-pointer outline-none focus:ring-2 focus:ring-sky-400 focus:scale-110 ${
                              isFinished
                                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-black focus:bg-emerald-400 focus:text-black'
                                : 'bg-white/10 hover:bg-sky-500 text-white hover:text-black focus:bg-sky-400 focus:text-black'
                            }`}
                          >
                            {isFinished ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <Play className="w-3.5 h-3.5 fill-current" />
                            )}
                          </button>

                          {/* Subtitles Button */}
                          <button
                            id={`modal-ep-sub-${idx}`}
                            data-focusable="true"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSubdlEpisode(ep);
                              setIsSubdlOpen(true);
                            }}
                            title="Cercar subtítols a SubDL per a aquest episodi"
                            className="p-2 rounded-lg bg-white/5 hover:bg-sky-500/20 text-slate-400 hover:text-sky-300 transition-colors cursor-pointer outline-none focus:ring-2 focus:ring-sky-400 focus:bg-sky-500 focus:text-black"
                          >
                            <Subtitles className="w-3.5 h-3.5" />
                          </button>

                          {/* Details Button */}
                          <button
                            id={`modal-ep-info-${idx}`}
                            data-focusable="true"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailEpisode({
                                ep,
                                targetIndex,
                                progressSeconds: epHistory?.progressSeconds || 0
                              });
                            }}
                            title="Veure fitxa i detalls de l'episodi"
                            className="p-2 rounded-lg bg-white/5 hover:bg-sky-500/20 text-slate-400 hover:text-sky-300 transition-colors cursor-pointer outline-none focus:ring-2 focus:ring-sky-400 focus:bg-sky-500 focus:text-black"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          ) : (
            /* STANDARD LAYOUT FOR MOVIES */
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {/* Poster thumbnail */}
                <div className="w-24 sm:w-28 md:w-32 aspect-[2/3] rounded-xl overflow-hidden shadow-xl shadow-black border border-white/20 shrink-0 bg-slate-800">
                  <img
                    src={item.poster}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>

                {/* Title & Metadata */}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-sky-500 text-black">
                      FILM
                    </span>
                    {item.collectionName && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30 flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        <span>{item.collectionName}</span>
                      </span>
                    )}
                    {item.originalTitle && (
                      <span className="text-[11px] text-slate-400 font-jp truncate max-w-[200px]">
                        {item.originalTitle}
                      </span>
                    )}
                  </div>

                  <h2 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight leading-tight">
                    {item.title}
                  </h2>

                  <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-300">
                    {item.rating && (
                      <div className="flex items-center gap-1 text-sky-400 font-bold bg-sky-500/10 px-1.5 py-0.5 rounded border border-sky-500/20 text-[11px]">
                        <Star className="w-3 h-3 fill-sky-400" />
                        <span>{item.rating.toFixed(1)}/10</span>
                      </div>
                    )}
                    {item.year && (
                      <div className="flex items-center gap-1 text-slate-400">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{item.year}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-slate-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{item.runtime || 90} min</span>
                    </div>
                  </div>

                  {/* Primary Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button
                      id="modal-play-primary"
                      data-focusable="true"
                      type="button"
                      onClick={() => onPlay(item, primaryEpIdx, primaryResumeTime)}
                      className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs sm:text-sm shadow-md shadow-sky-500/25 transition-all flex items-center gap-2 cursor-pointer outline-none focus:ring-2 focus:ring-sky-400"
                    >
                      <Play className="w-4 h-4 fill-white" />
                      <span>{primaryResumeTime > 0 ? 'Reprendre' : 'Reprodueix'}</span>
                    </button>

                    <button
                      id="modal-subdl-button"
                      data-focusable="true"
                      type="button"
                      tabIndex={0}
                      onClick={() => setIsSubdlOpen(true)}
                      className="px-3 py-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 border border-amber-500/40 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer outline-none focus:ring-2 focus:ring-amber-400"
                      title="Cercar subtítols a SubDL"
                    >
                      <Subtitles className="w-3.5 h-3.5 text-amber-400" />
                      <span>SubDL</span>
                    </button>

                    <button
                      id="modal-share-button"
                      data-focusable="true"
                      type="button"
                      tabIndex={0}
                      onClick={handleShare}
                      className="px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer outline-none focus:ring-2 focus:ring-sky-400"
                      title="Copiar enllaç"
                    >
                      {copiedLink ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
                      <span>{copiedLink ? 'Copiat!' : 'Compartir'}</span>
                    </button>
                  </div>

                </div>
              </div>

              {/* Synopsis */}
              <div className="space-y-1.5 pt-1">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Sinopsi</h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-h-40 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/10">
                  {item.summary || "Sense sinopsi disponible."}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Episode Detail Metadata Modal */}
      {detailEpisode && item && (
        <div 
          id="episode-detail-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in"
          onClick={() => setDetailEpisode(null)}
        >
          <div 
            className="relative w-full max-w-lg bg-[#0d1017] border border-white/10 rounded-3xl overflow-hidden shadow-2xl text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header backdrop / image if available */}
            {(detailEpisode.ep.poster || (detailEpisode.ep as any).movie_image || (detailEpisode.ep as any).image || item.backdrop) && (
              <div className="relative h-44 sm:h-52 w-full bg-slate-900 overflow-hidden">
                <img
                  src={detailEpisode.ep.poster || (detailEpisode.ep as any).movie_image || (detailEpisode.ep as any).image || item.backdrop}
                  alt={detailEpisode.ep.title || ''}
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1017] via-[#0d1017]/50 to-transparent" />
                <button
                  id="episode-detail-close-btn"
                  data-focusable="true"
                  type="button"
                  onClick={() => setDetailEpisode(null)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-black/70 hover:bg-white/20 text-white backdrop-blur-md transition-colors outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-6 right-6">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-sky-500/30 text-sky-300 border border-sky-500/40">
                    Temporada {detailEpisode.ep.season || selectedSeason} • Episodi {detailEpisode.ep.episode}
                  </span>
                  <h3 className="font-display font-bold text-xl sm:text-2xl text-white mt-1 drop-shadow-md">
                    {detailEpisode.ep.title || `Episodi ${detailEpisode.ep.episode}`}
                  </h3>
                </div>
              </div>
            )}

            <div className="p-6 space-y-4">
              {/* Meta tags */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                {(detailEpisode.ep.duration || detailEpisode.ep.runtime) && (
                  <span className="flex items-center gap-1 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5">
                    <Clock className="w-3.5 h-3.5 text-sky-400" />
                    <span>{detailEpisode.ep.duration || detailEpisode.ep.runtime} min</span>
                  </span>
                )}
                {detailEpisode.ep.rating && (
                  <span className="flex items-center gap-1 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5 text-amber-400 font-semibold">
                    <Star className="w-3.5 h-3.5 fill-amber-400" />
                    <span>{Number(detailEpisode.ep.rating).toFixed(1)} / 10</span>
                  </span>
                )}
                {detailEpisode.ep.air_date && (
                  <span className="flex items-center gap-1 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5 text-slate-300">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>{detailEpisode.ep.air_date}</span>
                  </span>
                )}
                {detailEpisode.ep.tmdb_id && (
                  <span className="text-[10px] text-slate-500 font-mono">
                    TMDB ID: {detailEpisode.ep.tmdb_id}
                  </span>
                )}
              </div>

              {/* Plot / Synopsis */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Sinopsi de l&apos;episodi</h4>
                <p className="text-sm text-slate-300 leading-relaxed max-h-40 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/10">
                  {detailEpisode.ep.plot || (detailEpisode.ep as any).summary || 'Sense sinopsi disponible per a aquest capítol.'}
                </p>
              </div>

              {/* Quick Actions inside detail */}
              <div className="pt-3 border-t border-white/10 flex items-center justify-end gap-2">
                <button
                  id="episode-detail-sub-btn"
                  data-focusable="true"
                  type="button"
                  onClick={() => {
                    const ep = detailEpisode.ep;
                    setDetailEpisode(null);
                    setSubdlEpisode(ep);
                    setIsSubdlOpen(true);
                  }}
                  className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold flex items-center gap-2 transition-all outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <Subtitles className="w-4 h-4 text-sky-400" />
                  <span>Subtítols SubDL</span>
                </button>

                <button
                  id="episode-detail-play-btn"
                  data-focusable="true"
                  type="button"
                  onClick={() => {
                    const epTargetIdx = detailEpisode.targetIndex;
                    const epProg = detailEpisode.progressSeconds;
                    setDetailEpisode(null);
                    onPlay(item, epTargetIdx, epProg);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold flex items-center gap-2 shadow-lg shadow-sky-500/20 transition-all outline-none focus:ring-2 focus:ring-white"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>Reproduir Episodi</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SubDL Subtitles Finder Modal */}
      {isSubdlOpen && item && (
        <SubdlSubtitleModal
          isOpen={isSubdlOpen}
          onClose={() => {
            setIsSubdlOpen(false);
            setSubdlEpisode(null);
          }}
          mediaTitle={item.title}
          originalTitle={item.originalTitle}
          mediaType={item.type}
          tmdbId={item.tmdbId}
          season={subdlEpisode ? (Number(subdlEpisode.season) || selectedSeason) : selectedSeason}
          episode={subdlEpisode ? Number(subdlEpisode.episode) : (filteredEpisodes[0]?.episode || 1)}
          episodeTitle={subdlEpisode?.title || filteredEpisodes[0]?.title}
          onSelectSubtitle={(_sub) => {
            setIsSubdlOpen(false);
            const targetEp = subdlEpisode || filteredEpisodes[0];
            const epIdx = targetEp ? episodes.indexOf(targetEp) : primaryEpIdx;
            const finalEpIdx = epIdx >= 0 ? epIdx : primaryEpIdx;
            const epHist = targetEp ? historyMap[`${item.id}_s${targetEp.season || 1}_e${targetEp.episode}`] : null;
            onPlay(item, finalEpIdx, epHist?.progressSeconds || (finalEpIdx === primaryEpIdx ? primaryResumeTime : 0));
            setSubdlEpisode(null);
          }}
        />
      )}
    </div>
  );
};
